/**
 * The pandit reliability checklist's per-maker "notifications help" — `/v1/admin/notification-help`
 * (CSR-109). The app reads `GET /v1/content/notification-help?manufacturer=&locale=`; these are the
 * steps behind it, one ordered list per maker group per locale, content-linted on the server.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type HelpGroup = components['schemas']['NotificationHelpGroupRow'];
export type HelpStep = components['schemas']['NotificationHelpStepIn'];
export type HelpLocale = 'en' | 'hi';

export function useNotificationHelp() {
  return useQuery({
    queryKey: keys.notificationHelp(),
    queryFn: () =>
      authedClient.request<components['schemas']['NotificationHelpGroupListResponse']>('/v1/admin/notification-help'),
  });
}

function useHelpWrite<V>(call: (v: V) => Promise<HelpGroup>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: call,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.notificationHelp() }),
  });
}

/** One group's steps in one locale, replaced whole; their order is the order the app shows. */
export const useReplaceHelpSteps = () =>
  useHelpWrite(({ group, locale, steps }: { group: string; locale: HelpLocale; steps: HelpStep[] }) =>
    authedClient.request<HelpGroup>(`/v1/admin/notification-help/${group}/steps/${locale}`, {
      method: 'PUT',
      body: { steps },
    }),
  );

export const useSetHelpMakers = () =>
  useHelpWrite(({ group, manufacturers }: { group: string; manufacturers: string[] }) =>
    authedClient.request<HelpGroup>(`/v1/admin/notification-help/${group}/manufacturers`, {
      method: 'PUT',
      body: { manufacturers },
    }),
  );
